import { useEffect, useState } from 'react';
import { getUsuariosTodos, createUsuario, updateUsuario, deleteUsuario, getAgencias, generarAccesoUsuario } from '../services/api';
import Modal from '../components/ui/Modal';
import WhatsAppAccesoModal, { DestinatarioAcceso } from '../components/WhatsAppAccesoModal';
import { useAuth } from '../context/AuthContext';

const ROLES = ['SuperAdmin', 'Admin', 'Gerente', 'Agente', 'Operaciones'];

const ROL_COLORS: Record<string, string> = {
  SuperAdmin:  'bg-purple-100 text-purple-700',
  Admin:       'bg-blue-100 text-blue-700',
  Gerente:     'bg-indigo-100 text-indigo-700',
  Agente:      'bg-green-100 text-green-700',
  Operaciones: 'bg-orange-100 text-orange-700',
};

const EMPTY_FORM = {
  nombre: '', email: '', password: '', resetPassword: '', rol: 'Agente',
  agenciaId: '', activo: true, agenteScrapeId: '', telefono: '',
};

export default function Usuarios() {
  const { user } = useAuth();
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [agencias, setAgencias] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [waAccesoDestinatarios, setWaAccesoDestinatarios] = useState<DestinatarioAcceso[] | null>(null);
  const [generandoAcceso, setGenerandoAcceso] = useState<number | null>(null);

  const load = () => getUsuariosTodos().then(res => setUsuarios(res.data));

  useEffect(() => {
    load();
    getAgencias().then(res => setAgencias(res.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (u: any) => {
    setEditingId(u.id);
    setForm({
      nombre: u.nombre || '',
      email: u.email || '',
      password: '',
      resetPassword: '',
      rol: u.rol || 'Agente',
      agenciaId: u.agencia?.id ? String(u.agencia.id) : '',
      activo: u.activo ?? true,
      agenteScrapeId: u.agenteScrapeId || '',
      telefono: u.telefono || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId != null) {
        await updateUsuario(editingId, {
          nombre: form.nombre,
          email: form.email,
          rol: form.rol,
          activo: form.activo,
          agenciaId: form.agenciaId ? Number(form.agenciaId) : null,
          nuevaPassword: form.resetPassword || null,
          agenteScrapeId: form.agenteScrapeId || null,
          telefono: form.telefono || null,
        });
      } else {
        await createUsuario({
          nombre: form.nombre,
          email: form.email,
          password: form.password,
          rol: form.rol,
          agenciaId: form.agenciaId ? Number(form.agenciaId) : null,
          agenteScrapeId: form.agenteScrapeId || null,
          telefono: form.telefono || null,
        });
      }
      setShowModal(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, nombre: string) => {
    if (!window.confirm(`¿Eliminar al usuario "${nombre}"?`)) return;
    await deleteUsuario(id);
    setUsuarios(prev => prev.filter(u => u.id !== id));
  };

  const handleEnviarAcceso = async (u: any) => {
    setGenerandoAcceso(u.id);
    try {
      const res = await generarAccesoUsuario(u.id);
      setWaAccesoDestinatarios([{
        id: res.data.id,
        nombre: res.data.nombre,
        email: res.data.email,
        passwordTemporal: res.data.passwordTemporal,
        telefono: res.data.telefono ?? null,
      }]);
    } catch {
      alert('Error al generar acceso');
    } finally {
      setGenerandoAcceso(null);
    }
  };

  const f = (key: Exclude<keyof typeof EMPTY_FORM, 'activo' | 'resetPassword' | 'telefono'>) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value })),
  });

  const needsAgencia = form.rol !== 'SuperAdmin';
  const availableRoles = isSuperAdmin ? ROLES : ['Agente'];
  const showAgenteScrapeId = form.rol === 'Agente';

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Usuarios</h2>
        <button
          onClick={openCreate}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Nuevo usuario
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Rol</th>
              <th className="px-4 py-3 text-left">Agencia</th>
              <th className="px-4 py-3 text-left">ID Scraper</th>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {usuarios.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{u.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ROL_COLORS[u.rol] ?? 'bg-gray-100 text-gray-600'}`}>
                    {u.rol}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.agencia?.nombre ?? '—'}</td>
                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.agenteScrapeId ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {u.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openEdit(u)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Editar
                    </button>
                    {u.rol === 'Agente' && (
                      <button
                        onClick={() => handleEnviarAcceso(u)}
                        disabled={generandoAcceso === u.id}
                        className="text-xs text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                        title="Generar contraseña temporal y enviar por WhatsApp"
                      >
                        {generandoAcceso === u.id ? '...' : '📱 Acceso'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(u.id, u.nombre)}
                      className="text-xs text-red-500 hover:text-red-700 font-medium"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {usuarios.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No hay usuarios registrados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {waAccesoDestinatarios && (
        <WhatsAppAccesoModal
          destinatarios={waAccesoDestinatarios}
          onClose={() => setWaAccesoDestinatarios(null)}
        />
      )}

      {showModal && (
        <Modal
          title={editingId != null ? 'Editar usuario' : 'Nuevo usuario'}
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input type="text" {...f('nombre')} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" {...f('email')} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {editingId == null ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                  <input type="password" {...f('password')} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña <span className="text-gray-400 font-normal">(dejar vacío para no cambiar)</span></label>
                  <input type="password" value={form.resetPassword}
                    onChange={e => setForm(p => ({ ...p, resetPassword: e.target.value }))}
                    placeholder="••••••••"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                <select {...f('rol')} required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {needsAgencia && (
                <div className={editingId == null ? '' : 'col-span-1'}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agencia</label>
                  <select {...f('agenciaId')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">Sin agencia</option>
                    {agencias.map((a: any) => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              {showAgenteScrapeId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID Agente (scraper)</label>
                  <input type="text" {...f('agenteScrapeId')} placeholder="ej: 10553"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono (WhatsApp)</label>
                <input type="text"
                  value={form.telefono}
                  onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                  placeholder="ej: 5512345678"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {editingId != null && (
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={form.activo}
                    onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <label htmlFor="activo" className="text-sm font-medium text-gray-700">Usuario activo</label>
                </div>
              )}
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : editingId != null ? 'Actualizar' : 'Crear usuario'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
